import { Injectable, NgModuleRef, inject } from '@angular/core';
import { BackendService } from '@df/api/backend.service';
import { Model } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { app } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { ucfirst } from '@df/util/fn/ucfirst.fn';
import { ICmsAdminAbstractProvider } from '@icms/admin/util/provider/icms-admin-abstract.provider';
import { ICmsClientDataModelService } from '@icms/client/data/icms-client-data-model.service';
import { ICmsNotificationService } from '@icms/ui/notification/icms-notification.service';
import { ICmsShortcutsService } from './icms-shortcuts.service';

export interface IShortcutItem {
    title: string;
    children: IShortcutChildItem[];
}

export const EShortcatsSection = {
    SystemActions: 'System actions',
    MagentoActions: 'Magento actions',
    Actions: 'Actions'
};

export interface IShortcutChildItem {
    command: string;
    info?: string;
    status?: Status;
    action: IShortcutAction;
}

export type TActionResult = void | undefined | IShortcutItem[] | 'reset' | 'back';
type IShortcutAction = () => TActionResult;

@Injectable()
export class ICmsShortcutsProvider extends ICmsAdminAbstractProvider<IShortcutItem[], Model> {
    protected override providerId = 'shortcuts';

    private backendService = inject(BackendService);
    private iCmsClientDataModelService = inject(ICmsClientDataModelService);
    private iCmsNotificationService = inject(ICmsNotificationService);
    private iCmsShortcutsService = inject(ICmsShortcutsService);

    private client = inject(Client);

    constructor() {
        super();
        this.subscriptions.add(this.client.isCms$.subscribe(() => this.invalidate()));
    }

    /**
     * Provides burger menu items
     */
    protected prepareValue(model?: Model): IShortcutItem[] {
        return [...this.getSaveActions(), ...this.getEditActions(model), ...this.getApiActions(model)];
    }

    private getApiActions(model?: Model): IShortcutItem[] {
        if (model) {
            const children: IShortcutChildItem[] = [];
            children.push({
                command: `API model data`,
                info: `see direct data from API for ${model.type} ${model.id}`,
                action: () => this.iCmsClientDataModelService.previewApiData(model)
            });

            const parentId = (model.data as IProductDataV2)?.parent;
            const parent = parentId && parentId !== model.id && model.resource.get(parentId);
            if (parent) {
                children.push({
                    command: `API parent data`,
                    info: `direct data from API for ${model.type} ${parentId}`,
                    action: () => this.iCmsClientDataModelService.previewApiData(parent)
                });
            }

            const familyIds = model.type === Product.type ? (model as Product).family.map(i => i.id) : [model.id];
            const flushModelsCacheStatus = new Status();
            children.push({
                command: `Flush API cache`,
                info: `for ${model.type} ${model.id}` + (familyIds.length > 1 ? ' and its family' : ''),
                status: flushModelsCacheStatus,
                action: () => {
                    this.wrapRequest(
                        () => this.iCmsClientDataModelService.flushApiCache(flushModelsCacheStatus, model.type, familyIds),
                        `${ucfirst(model.type)} ${model.id} cache flushed`,
                        `Failed to flush ${model.type} ${model.id} cache`
                    );
                }
            });

            if (['product', 'category', 'store'].includes(model.type)) {
                const reindexStatus = new Status();
                children.push({
                    command: `Reindex current model`,
                    info: `${model.type} ${model.id} and its family`,
                    status: reindexStatus,
                    action: () => {
                        this.wrapRequest(
                            () => this.backendService.sendReindexRequest(reindexStatus, model),
                            `${ucfirst(model.type)} ${model.id} reindexed`,
                            `Failed to reindex ${model.type} ${model.id}`
                        );
                    }
                });
            }

            return [
                {
                    title: EShortcatsSection.SystemActions,
                    children
                }
            ];
        }
        return [];
    }

    /**
     * Model edit in admin actions
     */
    private getEditActions(model?: Model): IShortcutItem[] {
        const children: IShortcutChildItem[] = [];
        if (model) {
            const editLink = this.backendService.getEditUrl(model);
            if (editLink) {
                children.push({
                    command: `Edit model in admin`,
                    info: `${model.type} ${model.id}`,
                    action: () => {
                        window.open(editLink, '_blank');
                    }
                });
            }
            const parentId = (model.data as IProductDataV2)?.parent;
            if (parentId && parentId !== model.id) {
                const parent = model.resource.get(parentId);
                const parentEditLinke = this.backendService.getEditUrl(parent);
                if (parentEditLinke) {
                    children.push({
                        command: `Edit parent in admin`,
                        info: `${parent.type} ${parent.id}`,
                        action: () => {
                            window.open(parentEditLinke, '_blank');
                        }
                    });
                }
            }
        }
        children.push({
            command: 'Access admin',
            info: 'go to Magento admin panel',
            action: () => {
                window.open(this.backendService.getAuthUrl(), '_blank');
            }
        });
        return [
            {
                title: EShortcatsSection.MagentoActions,
                children
            }
        ];
    }

    /**
     * Model edit in admin actions
     */
    private getSaveActions(): IShortcutItem[] {
        if (!this.client.isCms$.value) {
            return [];
        }
        const saveStatus = new Status();
        const publishStatus = new Status();
        return [
            {
                title: EShortcatsSection.Actions,
                children: [
                    {
                        command: 'Save',
                        info: 'drafts changes with selection',
                        action: () => {
                            this.iCmsShortcutsService.destroyComponent();
                            app.iCmsAdminDataSaveService.saveAll();
                        }
                    },
                    {
                        command: 'Publish',
                        info: 'active drafts with selection',
                        action: () => {
                            this.iCmsShortcutsService.destroyComponent();
                            app.iCmsAdminDataSaveService.publishAll();
                        }
                    },
                    {
                        command: 'Save forced',
                        info: 'all drafts changes without any prompt',
                        status: saveStatus,
                        action: () => {
                            this.wrapRequest(
                                () => app.iCmsAdminDataSaveService.forcedSaveAll(saveStatus),
                                'All drafts saved',
                                'Failed to save all drafts'
                            );
                        }
                    },
                    {
                        command: 'Save & publish forced',
                        info: 'all changes without any prompt',
                        status: publishStatus,
                        action: () => {
                            this.wrapRequest(
                                () => app.iCmsAdminDataSaveService.forcedPublishAll(publishStatus),
                                'All changes published',
                                'Failed to save and publish data, but part of it might succeed'
                            );
                        }
                    }
                ]
            }
        ];
    }

    private async wrapRequest(action: () => Promise<any>, successMsg: string, errorMsg: string) {
        try {
            await action();
            this.iCmsNotificationService.add({
                title: successMsg,
                type: 'success'
            });
        } catch (err) {
            this.iCmsNotificationService.add({
                title: errorMsg,
                type: 'error',
                copy: this.iCmsNotificationService.toErrorMsg(err),
                duration: 0
            });
        }
    }
}
